import { GridLegacy as Grid, Stack } from '@mui/material';
import { InviteeSummary } from './InviteeSummary';
import { UserSummaryNew } from './UserSummaryNew';
import { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useUserInfo from '../../../hooks/useUserInfo';
import { SweepNotificationVariant } from '../../../reducers/notificationsReducer';
import { selectUserInfoData } from '../../../reducers/userInfoReducer';
import { Template } from '../../../types/UserInfoTypes';
import { InviteUserInput } from './InviteUserInput';
import ConfirmDestructiveDialog from '../dialogs/ConfirmDestructiveDialog';
import { getIsUserInactive } from '../../../lib/getIsUserActive';
import { selectAccountUsersData } from '../../../reducers/accountUsersReducer';
import { telemetry } from '../../../telemetry';
import { selectRoleGroups } from '../../../reducers/roleGroupsReducer';
import { useInviteUsers } from './useInviteUsers';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { colors, Typography } from '@sweep-io/sweep-design';
import pluralize from 'pluralize';
import { SearchInput } from '../SearchInput';
import { AdvancedFilter } from '../filters/advanced-filter/AdvancedFilter';

export const removeInvitationMessages = {
  [SweepNotificationVariant.Success]: `Invitation removed successfully`,
  [SweepNotificationVariant.Error]: `Invitation removal failed, please try again later`,
};

export const removeUserMessages = {
  [SweepNotificationVariant.Success]: `User removed successfully`,
  [SweepNotificationVariant.Error]: `User removal failed, please try again later`,
};

export const resentMessages = (email: string) => ({
  [SweepNotificationVariant.Success]: `Invitation email to ${email} was sent successfully`,
  [SweepNotificationVariant.Error]: `Invite to Sweep failed, please try again`,
});

interface InviteUsersInputWithListProps {
  funnelMapName?: string;
  funnelMapId?: string;
  disableRoleDropdown?: boolean;
  template?: Template;
  messagePlaceholder?: string;
  hideInviteInput?: boolean;
}

export const InviteUsersInputWithList = ({
  funnelMapId,
  funnelMapName,
  disableRoleDropdown,
  template = Template.Account,
  messagePlaceholder,
  hideInviteInput,
}: InviteUsersInputWithListProps) => {
  const { multiAccounts } = useFeatureToggle();
  const { sendInvitation, updateUserRoles, removeInvitation, removeUser } = useUserInfo();
  const { sendNewInvitation, afterSendActions } = useInviteUsers({
    funnelMapId: funnelMapId ?? '',
    funnelMapName: funnelMapName ?? '',
    template,
  });

  const userInfo = useSelector(selectUserInfoData);
  const accountUsersData = useSelector(selectAccountUsersData);
  const roles = useSelector(selectRoleGroups);

  const orgUsers = useMemo(() => accountUsersData?.users || [], [accountUsersData]);
  const orgInvitations = useMemo(() => accountUsersData?.invitations || [], [accountUsersData]);

  const activeOrgUsersWithoutCurrent = useMemo(
    () => orgUsers.filter((user) => user.id !== userInfo?.id && !getIsUserInactive(user.status)),
    [orgUsers, userInfo],
  );

  const usersCount = useMemo(
    () => activeOrgUsersWithoutCurrent?.length + orgInvitations?.length + 1,
    [activeOrgUsersWithoutCurrent, orgInvitations],
  );

  const [userToRemove, setUserToRemove] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const roleFilterItems = useMemo(
    () => (roles ?? []).map((role) => ({ value: role.id, label: role.name })),
    [roles],
  );

  const matchesSearch = useCallback(
    (name?: string, email?: string) => {
      if (!searchText) return true;
      const lowerSearch = searchText.toLowerCase();
      return (
        name?.toLowerCase().includes(lowerSearch) || email?.toLowerCase().includes(lowerSearch)
      );
    },
    [searchText],
  );

  const matchesRole = useCallback(
    (roleGroupId?: string) => {
      if (selectedRoleIds.length === 0) return true;
      return !!roleGroupId && selectedRoleIds.includes(roleGroupId);
    },
    [selectedRoleIds],
  );

  const showCurrentUser =
    matchesSearch(userInfo?.name, userInfo?.email) && matchesRole(userInfo?.roleGroupId);

  const filteredUsers = useMemo(() => {
    return activeOrgUsersWithoutCurrent.filter(
      (user) => matchesSearch(user.name, user.email) && matchesRole(user.roleGroupId),
    );
  }, [activeOrgUsersWithoutCurrent, matchesSearch, matchesRole]);

  const filteredInvitations = useMemo(() => {
    return orgInvitations.filter(
      (inv) => matchesSearch(undefined, inv.invitee) && matchesRole(inv.roleGroupId),
    );
  }, [orgInvitations, matchesSearch, matchesRole]);

  const updateUserRole = useCallback(
    async (userId: string, newRole: string) => {
      await updateUserRoles(userId, newRole);
      afterSendActions(true);
    },
    [updateUserRoles, afterSendActions],
  );

  return (
    <>
      <ConfirmDestructiveDialog
        open={Boolean(userToRemove)}
        onConfirm={async () => {
          try {
            await removeUser(userToRemove);
            afterSendActions(false, removeUserMessages);
            setUserToRemove('');
          } catch (error) {
            telemetry.captureError(new Error('Error while removing user'), error);
            afterSendActions(true, removeUserMessages);
            setUserToRemove('');
          }
        }}
        onClose={() => setUserToRemove('')}
        title="Remove user"
        confirmText="Confirm"
        cancelText="Cancel"
      >
        Confirming this action will remove user permanently from your workspace.
      </ConfirmDestructiveDialog>

      {hideInviteInput ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            backgroundColor: colors.grey[100],
            borderRadius: '8px',
            p: 1,
            width: '100%',
            mb: 2,
          }}
        >
          <AdvancedFilter
            items={roleFilterItems}
            selectedItems={selectedRoleIds}
            onSelectedItemsChange={(items) => setSelectedRoleIds(items)}
            texts={{ title: 'Role' }}
          />
          <Stack sx={{ width: '300px' }}>
            <SearchInput
              withFixedMagnifyingGlassIcon
              placeholder="Search users"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClearButtonClick={() => setSearchText('')}
              size="small"
            />
          </Stack>
        </Stack>
      ) : (
        <InviteUserInput
          roles={roles ?? []}
          isAccountConnected={!!userInfo?.account?.id}
          onSendInvitationEmail={sendNewInvitation}
          afterSendActions={afterSendActions}
          messagePlaceholder={messagePlaceholder}
        />
      )}

      <Grid container flexDirection="column" gap={multiAccounts ? 0 : 1} overflow="auto">
        <Stack direction="row" alignItems="center" gap={1} pb={2}>
          <Typography variant="caption" color={colors.grey[700]}>
            {usersCount} {pluralize('user', usersCount)}
          </Typography>
        </Stack>

        {showCurrentUser && (
          <div data-testid="user-summary-current">
            <UserSummaryNew
              user={userInfo}
              roles={roles ?? []}
              onChangeRoleCb={updateUserRole}
              disableRoleDropdown //always disabled so current user won't change his role
            />
          </div>
        )}

        {filteredUsers.map((user, index) => (
          <div key={'user_' + user.id} data-testid={`user-summary-${index}`}>
            <UserSummaryNew
              user={user}
              roles={roles ?? []}
              onChangeRoleCb={updateUserRole}
              onUserRemove={setUserToRemove}
              disableRoleDropdown={disableRoleDropdown}
              showBorders={multiAccounts}
            />
          </div>
        ))}

        {filteredInvitations.map((user) => (
          <div key={user.invitee}>
            <InviteeSummary
              user={user}
              onResend={async (user) => {
                try {
                  await sendInvitation({
                    email: user.invitee,
                    roleGroupId: user.roleGroupId,
                    template: user.template,
                    message: user.message,
                    entryPointName: user.entryPointName,
                    entryPointId: user.entryPointId,
                  });
                  afterSendActions(false, resentMessages(user.invitee));
                } catch (error) {
                  telemetry.captureError(error);
                  afterSendActions(true, resentMessages(user.invitee));
                }
              }}
              onRemove={async (email) => {
                try {
                  const res = await removeInvitation(email);
                  afterSendActions(false, removeInvitationMessages);
                  return res;
                } catch (error) {
                  telemetry.captureError(error);
                  afterSendActions(true, removeInvitationMessages);
                }
              }}
            />
          </div>
        ))}

        {(searchText || selectedRoleIds.length > 0) &&
          !showCurrentUser &&
          filteredUsers.length === 0 &&
          filteredInvitations.length === 0 && (
            <Typography variant="body" color={colors.grey[700]}>
              No results found
            </Typography>
          )}
      </Grid>
    </>
  );
};
